import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useSignIn } from '@clerk/clerk-react'
import { ROUTES } from '@/routes/routes'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AuthLayout } from '@/components/auth/AuthLayout'
import { clerkErrorMessage } from '@/lib/clerkError'
import { completeAuthAndRedirect } from '@/lib/postAuthRedirect'

export default function ForgotPasswordPage() {
  const { isLoaded, signIn, setActive } = useSignIn()
  const navigate = useNavigate()

  const [step, setStep] = useState<'request' | 'reset' | 'totp'>('request')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)

  // create() with this strategy already sends the code itself — a follow-up
  // prepareFirstFactor() call (tried here previously) sends a *second* email with a
  // *different* code, and Clerk then verifies against whichever fired last, not whichever
  // email the user actually opened. One call, one email.
  // Returns whether a code was actually sent — false means the caller should show its own
  // "not available" message rather than treat it as a Clerk-thrown error.
  async function sendResetCode(): Promise<boolean> {
    // Non-null: only ever called from handlers that already checked isLoaded first.
    const result = await signIn!.create({
      strategy: 'reset_password_email_code',
      identifier: email,
    })
    return !!result.supportedFirstFactors?.some((f) => f.strategy === 'reset_password_email_code')
  }

  async function onRequestCode(e: React.FormEvent) {
    e.preventDefault()
    if (!isLoaded || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      if (await sendResetCode()) {
        setStep('reset')
      } else {
        setError('Password reset isn’t available for this email address.')
      }
    } catch (err) {
      setError(
        clerkErrorMessage(err, 'Unable to send a reset code. Please check the email address.')
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function onResendCode() {
    if (!isLoaded || resending) return
    setResending(true)
    setError(null)
    setResent(false)
    try {
      if (await sendResetCode()) {
        setResent(true)
      } else {
        setError('Password reset isn’t available for this email address.')
      }
    } catch (err) {
      setError(clerkErrorMessage(err, 'Failed to resend the code. Please try again.'))
    } finally {
      setResending(false)
    }
  }

  async function onResetPassword(e: React.FormEvent) {
    e.preventDefault()
    if (!isLoaded || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      let result = await signIn!.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code,
        password: newPassword,
      })
      // Verifying the code alone doesn't always also set the new password in the same step —
      // Clerk can come back wanting a separate, explicit call to actually apply it.
      if (result.status === 'needs_new_password') {
        result = await signIn!.resetPassword({ password: newPassword })
      }
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        await completeAuthAndRedirect(navigate, ROUTES.APP.DASHBOARD, 'email_password')
      } else if (result.status === 'needs_second_factor') {
        // Resetting the password doesn't waive an already-enrolled second factor — same as
        // signing in normally, Clerk still won't issue a session until TOTP is verified too.
        setStep('totp')
      } else {
        setError('That code didn’t complete the reset. Please try again.')
      }
    } catch (err) {
      setError(clerkErrorMessage(err, 'Invalid or expired code. Please try again.'))
      setCode('')
    } finally {
      setSubmitting(false)
    }
  }

  async function onSubmitTotp(e: React.FormEvent) {
    e.preventDefault()
    if (!isLoaded || totpCode.length !== 6 || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const result = await signIn!.attemptSecondFactor({ strategy: 'totp', code: totpCode })
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        await completeAuthAndRedirect(navigate, ROUTES.APP.DASHBOARD, 'email_password')
      } else {
        setError('That code didn’t complete sign-in. Please try again.')
        setTotpCode('')
      }
    } catch (err) {
      setError(clerkErrorMessage(err, 'Invalid code. Please try again.'))
      setTotpCode('')
    } finally {
      setSubmitting(false)
    }
  }

  if (step === 'totp') {
    return (
      <AuthLayout
        title="Two-factor authentication"
        subtitle="Your password was reset. Enter the 6-digit code from your authenticator app to finish signing in."
      >
        <form onSubmit={onSubmitTotp} className="space-y-4">
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="one-time-code"
            autoFocus
            placeholder="000000"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="text-center font-mono text-2xl tracking-widest text-[var(--fl-heading)]"
            aria-label="6-digit verification code"
          />

          {error && (
            <div className="rounded-md bg-[var(--fl-error-bg)] px-4 py-3 text-sm text-[var(--fl-error)]">
              {error}
            </div>
          )}

          <Button type="submit" disabled={totpCode.length !== 6 || submitting} className="w-full">
            {submitting ? 'Verifying…' : 'Verify'}
          </Button>
        </form>
      </AuthLayout>
    )
  }

  if (step === 'reset') {
    return (
      <AuthLayout
        title="Check your email"
        subtitle={`Enter the code we sent to ${email}, and choose a new password.`}
      >
        <form onSubmit={onResetPassword} className="space-y-4">
          <div className="space-y-1">
            <label htmlFor="code" className="text-sm font-medium text-[var(--fl-heading)]">
              Verification code
            </label>
            <Input
              id="code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center font-mono text-2xl tracking-widest text-[var(--fl-heading)]"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="newPassword" className="text-sm font-medium text-[var(--fl-heading)]">
              New password
            </label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>

          {error && (
            <div className="rounded-md bg-[var(--fl-error-bg)] px-4 py-3 text-sm text-[var(--fl-error)]">
              {error}
            </div>
          )}

          <Button type="submit" disabled={code.length !== 6 || submitting} className="w-full">
            {submitting ? 'Resetting…' : 'Reset password'}
          </Button>
        </form>

        <p className="text-xs text-[var(--fl-text-3)] text-center">
          {resent ? (
            'Sent — check your email.'
          ) : (
            <>
              Didn't get it?{' '}
              <button
                type="button"
                onClick={onResendCode}
                disabled={resending}
                className="text-[var(--fl-primary)] hover:underline disabled:opacity-50"
              >
                {resending ? 'Resending…' : 'Resend code'}
              </button>
            </>
          )}
        </p>

        <p className="text-xs text-[var(--fl-text-3)] text-center">
          <Link to={ROUTES.AUTH.SIGN_IN} className="text-[var(--fl-primary)] hover:underline">
            Back to sign in
          </Link>
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a code to reset your password."
    >
      <form onSubmit={onRequestCode} className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="email" className="text-sm font-medium text-[var(--fl-heading)]">
            Email
          </label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            autoComplete="email"
            required
          />
        </div>

        {error && (
          <div className="rounded-md bg-[var(--fl-error-bg)] px-4 py-3 text-sm text-[var(--fl-error)]">
            {error}
          </div>
        )}

        <Button type="submit" disabled={!isLoaded || submitting} className="w-full">
          {submitting ? 'Sending…' : 'Send reset code'}
        </Button>
      </form>

      <p className="text-xs text-[var(--fl-text-3)] text-center">
        <Link to={ROUTES.AUTH.SIGN_IN} className="text-[var(--fl-primary)] hover:underline">
          Back to sign in
        </Link>
      </p>
    </AuthLayout>
  )
}
